import React from 'react'
import styled from 'styled-components'
import pluralize from 'pluralize'
import accounting from 'accounting'

import InfoOutline from 'components/icons/InfoOutline'
import { formatPrice } from '../utils/pricing'
import { slugToNice } from '../utils/stringUtils'
import Tooltip from '../display/Tooltip'
import Price from './Price'
import LineItemLabel from './LineItemLabel'
import { Row, LabelColumn, PriceColumn } from './styles'
import { PricingProps, LineItemsProps, ListingProps } from './types'

const TooltipContent = styled.div`
  font-size: 14px;
  width: 200px;
`

const Wrapper = styled.div`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 300;
  padding-top: 16px;
`

const PricingTotal = ({
  pricing,
  user,
  doNotShowPayoutTotal = false,
}: {
  pricing: Pick<
    PricingProps,
    | 'status'
    | 'paymentStatus'
    | 'totalToPay'
    | 'currency'
    | 'payoutTotal'
    | 'total'
  >
  user: {
    firstName: string
  }
  doNotShowPayoutTotal: boolean
}) => {
  const currencyLabel = pricing.currency ? `(${pricing.currency})` : ''

  if (pricing.totalToPay) {
    const label =
      pricing.status === 'initiated' || pricing.paymentStatus !== 'paid'
        ? 'Total'
        : 'Total paid'
    const paymentError =
      pricing.status === 'accepted' && pricing.paymentStatus === 'failed'
    return (
      <Row error={paymentError} bold>
        <LabelColumn>
          {label} {currencyLabel}
        </LabelColumn>
        <PriceColumn>
          <Price value={pricing.totalToPay} />
        </PriceColumn>
      </Row>
    )
  }
  if (!doNotShowPayoutTotal && user && pricing.payoutTotal && pricing.total) {
    return (
      <React.Fragment>
        <Row>
          <LabelColumn>{user.firstName} will pay</LabelColumn>
          <PriceColumn>
            <Price value={pricing.total} />
          </PriceColumn>
        </Row>

        <Row bold>
          <LabelColumn>Your payout</LabelColumn>
          <PriceColumn>
            <Price value={pricing.payoutTotal} />
          </PriceColumn>
        </Row>
      </React.Fragment>
    )
  }
  if (!doNotShowPayoutTotal && pricing.payoutTotal) {
    return (
      <Row bold>
        <LabelColumn>Total payout</LabelColumn>
        <PriceColumn>
          <Price value={pricing.payoutTotal} />
        </PriceColumn>
      </Row>
    )
  }
  return (
    <Row bold>
      <LabelColumn>Total {currencyLabel}</LabelColumn>
      <PriceColumn>
        <Price value={pricing.total} />
      </PriceColumn>
    </Row>
  )
}

const CustomerServiceFee = ({ value }: { value?: string }) => {
  const serviceFee = parseFloat(value)
  const serviceFeeIncluded = serviceFee === 0

  return (
    <Row highlight={serviceFeeIncluded}>
      <LabelColumn>
        <span>Riparide service fee</span>
        <Tooltip zIndex={3} icon={<InfoOutline />}>
          <TooltipContent>
            <div>
              {serviceFeeIncluded ? (
                <p style={{ margin: 0 }}>
                  The host will cover the guest service fee so there&apos;s
                  nothing extra for you to pay 🙌
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  This helps us pay the bills, run our platform and build cool
                  new features. It includes tax.
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </LabelColumn>
      <PriceColumn>
        {serviceFeeIncluded ? 'FREE' : <Price value={serviceFee} />}
      </PriceColumn>
    </Row>
  )
}

const HostServiceFee = ({ value }: { value: number }) => (
  <Row>
    <LabelColumn>
      <span>Riparide service fee</span>
      <Tooltip zIndex={3} icon={<InfoOutline />}>
        <TooltipContent>
          <div>
            <p style={{ margin: 0 }}>
              This helps us pay the bills, run our platform and build cool new
              features. It includes tax.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </LabelColumn>
    <PriceColumn>
      <Price value={value} />
    </PriceColumn>
  </Row>
)

const Tax = ({
  value,
  details,
}: {
  value: PricingProps['taxAmount']
  details: PricingProps['taxRateDetails']
}) => {
  const exclusiveRates = details?.filter((d) => !d.included)
  if (exclusiveRates?.length === 1) {
    return (
      <Row>
        <LabelColumn>
          <span>{exclusiveRates[0].name}</span>
        </LabelColumn>
        <PriceColumn>
          <Price value={value} />
        </PriceColumn>
      </Row>
    )
  }
  return (
    <Row>
      <LabelColumn>
        <span>Tax</span>
        {exclusiveRates?.length > 0 && (
          <Tooltip zIndex={3} icon={<InfoOutline />}>
            <TooltipContent>
              <Wrapper>
                {exclusiveRates
                  .filter((d) => d.rate > 0)
                  .map((detail) => (
                    <Row key={detail.name}>
                      <LabelColumn>{detail.name}</LabelColumn>
                      <PriceColumn>
                        {accounting.toFixed(detail.rate * 100, 2)}%
                      </PriceColumn>
                    </Row>
                  ))}
              </Wrapper>
            </TooltipContent>
          </Tooltip>
        )}
      </LabelColumn>
      <PriceColumn>
        <Price value={value} />
      </PriceColumn>
    </Row>
  )
}

const ItemDetails = ({ details }: { details: LineItemsProps['details'] }) => (
  <Wrapper>
    {details.map((detail) => (
      <Row key={detail.label}>
        <LabelColumn>{detail.label}</LabelColumn>
        <PriceColumn>
          <Price value={detail.price} />
        </PriceColumn>
      </Row>
    ))}
  </Wrapper>
)

const RentalFeeTip = ({
  item,
  hint,
}: {
  item: Pick<LineItemsProps, 'details'>
  hint?: PricingProps['hint']
}) =>
  (item.details && item.details.length > 1) || hint ? (
    <Tooltip zIndex={3} icon={<InfoOutline />}>
      <TooltipContent>
        {item.details.length > 1 && <ItemDetails details={item.details} />}
        <p>{hint}</p>
      </TooltipContent>
    </Tooltip>
  ) : null

const CleaningFeeTip = () => (
  <Tooltip zIndex={3} icon={<InfoOutline />}>
    <TooltipContent>
      <span>A one-off fee for cleaning the property.</span>
    </TooltipContent>
  </Tooltip>
)

const ExtraPersonFeeTip = ({
  item,
  listing,
}: {
  item: Pick<LineItemsProps, 'details'>
  listing: Pick<
    ListingProps,
    'extraPersonThreshold' | 'extraPersonFee' | 'pricingSuffix'
  >
}) => (
  <Tooltip zIndex={3} icon={<InfoOutline />}>
    <TooltipContent>
      {!item.details && (
        <span>
          The base price is for{' '}
          {pluralize('person', listing.extraPersonThreshold, true)}. A fee of{' '}
          {formatPrice(listing.extraPersonFee)}&nbsp;
          {slugToNice(listing.pricingSuffix)} applies for each additional guest.
        </span>
      )}
      {item.details && <ItemDetails details={item.details} />}
    </TooltipContent>
  </Tooltip>
)

const DiscountTip = ({ item }: { item: Pick<LineItemsProps, 'details'> }) => (
  <Tooltip zIndex={3} icon={<InfoOutline />}>
    <TooltipContent>
      {item.details &&
        item.details.map((detail) => (
          <span key={detail.label}>{detail.label}</span>
        ))}
    </TooltipContent>
  </Tooltip>
)

const GiftVouchersTip = ({
  vouchers,
}: {
  vouchers: PricingProps['vouchers']
}) => (
  <Tooltip zIndex={3} icon={<InfoOutline />}>
    <TooltipContent>
      <Wrapper>
        {vouchers.map((voucher) => (
          <Row key={voucher.code}>
            <LabelColumn>{voucher.code}</LabelColumn>
            <PriceColumn>
              <Price value={voucher.amount} />
            </PriceColumn>
          </Row>
        ))}
      </Wrapper>
    </TooltipContent>
  </Tooltip>
)

const PricingBreakdown = ({
  pricing,
  user,
  doNotShowPayoutTotal,
  showTax = true,
}: {
  pricing: PricingProps
  user?: {
    id: string
    firstName: string
  }
  doNotShowPayoutTotal?: boolean
  showTax?: boolean
}) => {
  const { lineItems, hint, listing } = pricing

  return (
    <Wrapper className="qa-pricing-breakdown">
      {lineItems.map((item) => (
        <Row key={item.itemType}>
          <LabelColumn>
            <LineItemLabel item={item} pricingSuffix={listing.pricingSuffix} />
            {(item.itemType === 'rental_fee' ||
              item.itemType === 'rental_fee') && (
              <RentalFeeTip item={item} hint={hint} />
            )}
            {item.itemType === 'discount' && <DiscountTip item={item} />}
            {item.itemType === 'cleaning_fee' && <CleaningFeeTip />}
            {listing.extraPersonFee && item.itemType === 'extra_person_fee' && (
              <ExtraPersonFeeTip item={item} listing={listing} />
            )}
          </LabelColumn>

          <PriceColumn>
            <Price value={item.amount || item.price} />
          </PriceColumn>
        </Row>
      ))}
      {parseFloat(pricing.hostServiceFee) > 0 ? (
        <HostServiceFee value={-pricing.hostServiceFee} />
      ) : (
        <CustomerServiceFee value={pricing.customerServiceFee} />
      )}
      {showTax && parseFloat(pricing.taxAmount) > 0 && (
        <Tax value={pricing.taxAmount} details={pricing.taxRateDetails} />
      )}
      {parseFloat(pricing.voucherAmount) > 0 && (
        <Row>
          <LabelColumn>
            <span>Gift voucher</span>
            {pricing.vouchers && (
              <GiftVouchersTip vouchers={pricing.vouchers} />
            )}
          </LabelColumn>
          <PriceColumn>
            <Price value={-pricing.voucherAmount} />
          </PriceColumn>
        </Row>
      )}
      <PricingTotal
        pricing={pricing}
        user={user}
        doNotShowPayoutTotal={doNotShowPayoutTotal}
      />
    </Wrapper>
  )
}

export default PricingBreakdown
